import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Apollo } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import {
  DeleteWarehouseDocument,
  DeleteWarehouseMutationVariables,
  GenericNumber,
  GetAllSummaryWarehouseDocument,
  GetAllWarehouseTypeDocument,
  GetAllWarehouseTypeQuery,
  GetNextWarehouseNumberDocument,
  GetNextWarehouseNumberQuery,
  GetWarehouseByBusinessUnitIdDocument,
  GetWarehouseByIdDocument,
  IdDto,
  SaveWarehouseDocument,
  SaveWarehouseMutation,
  SaveWarehouseMutationVariables,
  UpdateWarehouseDocument,
  UpdateWarehouseMutation,
  UpdateWarehouseMutationVariables,
  Warehouse,
  WarehouseIn,
  WarehouseType,
} from 'src/app/graphql/types';

@Injectable({
  providedIn: 'root',
})
export class WarehouseService {
  private readonly apollo = inject(Apollo);

  getById(id: number): Observable<Warehouse | null> {
    return this.apollo
      .watchQuery<{ getWarehouseById: Warehouse }>({
        query: GetWarehouseByIdDocument,
        variables: {
          id: id,
        },
      })
      .valueChanges.pipe(
        map(result => {
          return result.data ? result.data.getWarehouseById : null;
        }),
      );
  }

  getAllByBusinessUnitId(
    businessUnitId: number,
  ): Observable<Warehouse[] | null> {
    return this.apollo
      .watchQuery<{ getWarehouseByBusinessUnitId: Warehouse[] }>({
        query: GetWarehouseByBusinessUnitIdDocument,
        variables: {
          businessUnitId: businessUnitId,
        },
      })
      .valueChanges.pipe(
        map(result => {
          return result.data ? result.data.getWarehouseByBusinessUnitId : null;
        }),
      );
  }

  getAllSummaryWarehouse(
    businessUnitId: number,
  ): Observable<Warehouse[] | null> {
    return this.apollo
      .watchQuery<{ getWarehouseByBusinessUnitId: Warehouse[] }>({
        query: GetAllSummaryWarehouseDocument,
        variables: {
          businessUnitId: businessUnitId,
        },
      })
      .valueChanges.pipe(
        map(result => {
          return result.data ? result.data.getWarehouseByBusinessUnitId : null;
        }),
      );
  }

  getRecords(businessUnitId: number): Observable<Warehouse[] | null> {
    return this.apollo
      .watchQuery<{ getWarehouseByBusinessUnitId: Warehouse[] }>({
        query: GetWarehouseByBusinessUnitIdDocument,
        variables: {
          businessUnitId: businessUnitId,
        },
      })
      .valueChanges.pipe(
        map(result => {
          return result.data ? result.data.getWarehouseByBusinessUnitId : null;
        }),
      );
  }

  getWarehouseNumber(businessUnitId: number): Observable<GenericNumber | null> {
    return this.apollo
      .watchQuery<GetNextWarehouseNumberQuery>({
        query: GetNextWarehouseNumberDocument,
        variables: {
          businessUnitId: businessUnitId,
        },
      })
      .valueChanges.pipe(
        map(result => {
          if (result.data && result.data.getNextWarehouseNumber) {
            return result.data.getNextWarehouseNumber;
          } else {
            return null;
          }
        }),
      );
  }

  getAllWarehouseType(): Observable<WarehouseType[] | null> {
    return this.apollo
      .watchQuery<GetAllWarehouseTypeQuery>({
        query: GetAllWarehouseTypeDocument,
      })
      .valueChanges.pipe(
        map(result => {
          return result.data && result.data.getAllWarehouseType
            ? (result.data.getAllWarehouseType as WarehouseType[])
            : null;
        }),
      );
  }

  save(form: FormGroup): Observable<IdDto | null> {
    return this.apollo
      .mutate<SaveWarehouseMutation, SaveWarehouseMutationVariables>({
        mutation: SaveWarehouseDocument,
        variables: {
          json: this.jsonIn(form),
        },
      })
      .pipe(
        map(result => {
          return result.data ? (result.data.saveWarehouse as IdDto) : null;
        }),
      );
  }

  update(form: FormGroup): Observable<IdDto | null> {
    return this.apollo
      .mutate<UpdateWarehouseMutation, UpdateWarehouseMutationVariables>({
        mutation: UpdateWarehouseDocument,
        variables: {
          json: this.jsonIn(form),
        },
      })
      .pipe(
        map(result => {
          return result.data ? (result.data.updateWarehouse as IdDto) : null;
        }),
      );
  }

  delete(id: number): Observable<boolean | null> {
    return this.apollo
      .mutate<{ deleteWarehouse: boolean }, DeleteWarehouseMutationVariables>({
        mutation: DeleteWarehouseDocument,
        variables: {
          id: id.toString(),
        },
      })
      .pipe(
        map(result => {
          return result.data ? result.data.deleteWarehouse : null;
        }),
      );
  }

  jsonIn(form: FormGroup): WarehouseIn {
    return {
      active: form.get('active')?.value,
      businessUnitId: form.get('businessUnitId')?.value,
      id: form.get('id')?.value,
      name: form.get('name')?.value,
      type: { id: form.get('type')?.value },
      user: { id: form.get('user')?.value },
    };
  }
}
